<!-- src/apps/secret/components/layout/BrandedMastHead.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { isSsoEnabled, isOrgsSsoEnabled } from '@/utils/features';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { ref, computed } from 'vue';
  import { storeToRefs } from 'pinia';

  const { t } = useI18n();

  const productIdentity = useProductIdentity();
  const bootstrapStore = useBootstrapStore();
  const { authentication, homepage_config } = storeToRefs(bootstrapStore);
  const imageError = ref(false);

  /**
   * Per-domain narrowing for the auth nav links. Defaults to enabled when
   * no homepage_config exists for the domain. The frontend ANDs the system
   * authentication flags with these domain-level toggles — the domain owner
   * can only narrow, never broaden.
   */
  const domainSignupEnabled = computed(() => homepage_config.value?.signup_enabled !== false);
  const domainSigninEnabled = computed(() => homepage_config.value?.signin_enabled !== false);

  /**
   * Show Sign In link when signin route is available AND the domain hasn't
   * disabled it AND either:
   * - Platform authentication is enabled (authentication.enabled), OR
   * - Platform-level SSO is configured (features.sso.enabled), OR
   * - Domain-level SSO is enabled (features.organizations.sso_enabled)
   *
   * This ensures custom domains with SSO see the sign-in link even when
   * platform-level AUTH_ENABLED=false.
   */
  const showSignIn = computed(() => {
    const hasSigninRoute = authentication.value?.signin === true;
    const platformAuthEnabled = authentication.value?.enabled === true;
    const platformSsoEnabled = isSsoEnabled();
    const domainSsoEnabled = isOrgsSsoEnabled();

    return hasSigninRoute
      && domainSigninEnabled.value
      && (platformAuthEnabled || platformSsoEnabled || domainSsoEnabled);
  });

  /**
   * Sign Up requires platform authentication — SSO users are provisioned
   * through their identity provider, not the platform signup flow, so we
   * mirror the canonical MastHead gating here rather than the broader SSO
   * gating used for Sign In.
   */
  const showSignUp = computed(() =>
    authentication.value?.enabled === true
    && authentication.value?.signup === true
    && domainSignupEnabled.value
  );

  const handleImageError = () => {
    imageError.value = true;
  };

  interface Props extends LayoutProps {
    headertext: string;
    subtext: string;
  }

  withDefaults(defineProps<Props>(), {
    displayMasthead: true,
    displayNavigation: true,
    headertext: 'secure-links',
    subtext: 'a-trusted-way-to-share-sensitive-information-etc',
  });
</script>

<template>
  <div class="relative bg-white py-8 transition-colors duration-200 dark:bg-gray-900">
    <!-- Auth Links (Sign Up / Sign In for custom domain users) -->
    <nav
      v-if="showSignUp || showSignIn"
      class="absolute right-4 top-4 flex items-center gap-2"
      role="navigation"
      :aria-label="t('web.layout.main_navigation')">
      <router-link
        v-if="showSignUp"
        to="/signup"
        :title="t('web.homepage.signup_individual_and_business_plans')"
        data-testid="branded-signup-link"
        class="text-sm font-bold text-gray-600 transition-colors duration-200
          hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
        {{ t('web.COMMON.header_create_account') }}
      </router-link>
      <span
        v-if="showSignUp && showSignIn"
        class="text-sm text-gray-400"
        aria-hidden="true"
        role="separator">
        |
      </span>
      <router-link
        v-if="showSignIn"
        to="/signin"
        :title="t('web.homepage.log_in_to_onetime_secret')"
        data-testid="branded-signin-link"
        class="text-sm text-gray-600 transition-colors duration-200
          hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
        {{ t('web.COMMON.header_sign_in') }}
      </router-link>
    </nav>

    <div class="container mx-auto max-w-2xl px-4">
      <div class="flex flex-col items-center gap-8">
        <!-- Logo Section -->
        <div
          class="relative"
          role="region"
          :aria-label="t('web.layout.brand_logo')">
          <router-link to="/">
            <div
              :class="[
                productIdentity.cornerClass,
                'flex size-16 items-center justify-center overflow-hidden bg-gray-100 transition-all duration-200 dark:bg-gray-800',
              ]">
              <img
                v-if="productIdentity.logoUri && !imageError"
                :src="productIdentity.logoUri"
                alt=""
                class="size-16 object-contain"
                :class="productIdentity.cornerClass"
                @error="handleImageError" />
              <!-- Updated placeholder icon to match SecretPreview -->
              <svg
                v-else
                class="size-8 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </router-link>
        </div>

        <!-- Content Section -->
        <div
          class="space-y-3 text-center"
          :class="[
            productIdentity.fontFamilyClass,
            productIdentity.cornerClass,
          ]">
          <h1
            class="text-2xl font-medium text-gray-900 dark:text-gray-100 sm:text-3xl"
            :class="productIdentity.fontFamilyClass">
            {{ headertext }}
          </h1>
          <p
            class="mx-auto max-w-md text-sm text-gray-600 dark:text-gray-300 sm:text-base">
            {{ subtext }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
